import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuthUser } from "../utils/auth";
import {
  readStoredTier,
  PACKAGE_TIER_EVENT,
  PACKAGE_TIER_STORAGE_KEY,
} from "../utils/packageTier";

const navItems = [
{
  id: "roadmap",
  label: "Roadmap",
  route: "/roadmap",
  icon: ({ active }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth={active ? 2.2 : 1.6}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 7.5 9 5.25l6 2.25 5-2.25v11.25l-5 2.25-6-2.25-5 2.25V7.5Z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 5.25v11.25"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 7.5v11.25"
      />
    </svg>
  ),
},
{
  id: "rewards",
  label: "Program",
  route: "/rewards",
  icon: ({ active }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth={active ? 2.2 : 1.6}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236c-1.021.183-2.159.248-3.25.248v.25A6.01 6.01 0 0 1 5.25 4.236Z"
      />
    </svg>
  ),
},
 {
    id: "dashboard",
    label: "Dashboard",
    route: "/dashboard",
    variant: "center",
    icon: ({ active }) => (
      <div
        className={`grid h-12 w-12 place-items-center rounded-full border ${
          active ? "border-violet-500 bg-violet-500/10" : "border-white/15 bg-white/5"
        }`}
      >
        <img
          src="/images/synergy.png"
          alt="Dashboard"
          className="h-7 w-7 object-contain"
        />
      </div>
    ),
},
{
 id: "packages",
 label: "Packages",
 route: "/dashboard/packages",
  icon: ({ active }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth={active ? 2.2 : 1.6}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20.25 7.5 12 3 3.75 7.5m16.5 0-8.25 4.5m8.25-4.5V18l-8.25 4.5m0-10.5L3.75 7.5m8.25 4.5V22.5M3.75 7.5V18l8.25 4.5"
      />
    </svg>
  ),
},
 
{
  id: "referrer",
  label: "Referrals",
  route: "/referrer-link",
  icon: ({ active }) => (
    <img
      src="/images/reference.svg" // your image path
      alt="Referrals"
      className={`h-5 w-5 ${active ? 'opacity-100' : 'opacity-70'} transition-opacity`}
    />
  ),
},

  // {
  //   id: "profile",
  //   label: "Profile",
  //   route: "/profile",
  //   icon: ({ active }) => (
  //     <svg
  //       xmlns="http://www.w3.org/2000/svg"
  //       viewBox="0 0 24 24"
  //       className="h-5 w-5"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth={active ? 2.2 : 1.6}
  //     >
  //       <path
  //         strokeLinecap="round"
  //         strokeLinejoin="round"
  //         d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm-6 8a6 6 0 0 1 12 0"
  //       />
  //     </svg>
  //   ),
  // },
];

const allowedPrefixes = [
  "/dashboard",
  "/dashboard/packages",
  "/transactions",
  "/referrer-link",
  "/community",
  "/roadmap",
  "/rewards",
  "/profile",
];

const shouldShowNav = (pathname) => allowedPrefixes.some((prefix) => pathname.startsWith(prefix));

export default function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState("dashboard");
  const [minTier, setMinTier] = useState(0);

  // Resolve current user's tier (0 = no qualifying package, 2 = $200, 3 = $500)
  const refreshTier = useCallback(() => {
    const user = getAuthUser();
    const tierFromUser = Number(user?.packageTier);
    const userId = user?.id;
    const resolved = Number.isFinite(tierFromUser) ? tierFromUser : readStoredTier(userId) ?? 0;
    setMinTier(Number.isFinite(resolved) ? resolved : 0);
  }, []);

  useEffect(() => {
    refreshTier();

    const handleTierEvent = (event) => {
      if (!event || !event.detail) {
        refreshTier();
        return;
      }
      const next = Number(event.detail.tier);
      if (Number.isFinite(next)) {
        setMinTier(next);
      } else {
        refreshTier();
      }
    };

    const handleStorage = (event) => {
      if (event.key === PACKAGE_TIER_STORAGE_KEY) {
        refreshTier();
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener(PACKAGE_TIER_EVENT, handleTierEvent);
      window.addEventListener("storage", handleStorage);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener(PACKAGE_TIER_EVENT, handleTierEvent);
        window.removeEventListener("storage", handleStorage);
      }
    };
  }, [refreshTier]);

  const shouldRender = useMemo(() => shouldShowNav(location.pathname), [location.pathname]);

  const filteredItems = useMemo(() => {
    // Gate community and referrals at tier >= 2
    if (minTier >= 2) {
      return navItems;
    }
    return navItems.filter((item) => item.id !== "community" && item.id !== "referrer");
  }, [minTier]);

  const handleClick = useCallback(
    (itemId) => {
      setActive(itemId);
      const item = filteredItems.find((entry) => entry.id === itemId);
      if (!item?.route) return;

      if (!location.pathname.startsWith(item.route)) {
        navigate(item.route);
      }
    },
    [location.pathname, navigate, filteredItems],
  );

  useEffect(() => {
    if (!shouldRender) return;
    const matched = filteredItems.find((item) => item.route && location.pathname.startsWith(item.route));
    if (matched) {
      setActive(matched.id);
    }
  }, [location.pathname, shouldRender, filteredItems]);

  if (!shouldRender) return null;

  return (
    <div className="fixed inset-x-0 bottom-4 z-[60] flex justify-center md:hidden">
      <nav className="relative flex w-[92%] max-w-[420px] items-center justify-between rounded-full border border-white/10 bg-[#0B0A14]/95 px-5 py-3 shadow-[0_20px_45px_rgba(6,3,18,0.45)] backdrop-blur">
        {filteredItems.map((item) => {
          const isActive = active === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleClick(item.id)}
              className={`relative flex flex-col items-center gap-1 text-[10px] font-medium transition ${
                isActive ? "text-white" : "text-white/50 hover:text-white/80"
              }`}
            >
              {item.variant === "center" ? (
                item.icon({ active: isActive })
              ) : (
                <div
                  className={`grid h-9 w-9 place-items-center rounded-2xl ${
                    isActive ? "bg-violet-500/15 text-violet-400" : "bg-white/5"
                  }`}
                >
                  {item.icon({ active: isActive })}
                </div>
              )}
              <span className="sr-only">{item.label}</span>
              {isActive && item.variant !== "center" && (
                <span className="absolute -bottom-1 h-0.5 w-6 rounded-full bg-violet-500" />
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
